// PO7-S3 (docs/build-briefs/PO7-S3-invoice-review-and-variance.md; ADR-013
// Slice S3). The invoice review screen, built after
// design/mockups/po-merge-invoice-invoice-review.html: the duplicate banner,
// Matched, Needs your attention, Held back, and the totals location with Confirm
// invoice. It is rendered inside the Invoice card (ui/src/invoiceCard.ts), which
// owns the network and the permission snapshot; this file only draws what the
// server derived and hands a reviewer's tap back through `onAction`.
//
// PO7-S4 (docs/build-briefs/PO7-S4-invoice-confirm-and-cost.md): Confirm invoice
// is enabled when the server says nothing blocks and the session may confirm. A
// tap opens a confirm step (it is a financial write), and a confirmed invoice
// shows a Confirmed panel: the order's estimate beside the final invoice figure
// and, per line, what happened to the item's cost. The mockup draws only the
// disabled button, so the confirm step and the panel are drawn from the existing
// review styles and are for the designer.
//
// PO7-S4-F1 (docs/build-briefs/PO7-S4-F1-invoice-line-kinds.md, DEC-299): an
// unmatched line also offers Fee, credit or tax, which opens one sheet of
// fixed kind tiles (nothing preselected), Leave this line out and Cancel.
// Classified lines move to Other lines (Change kind), the totals location becomes
// Estimated versus final with a Left out row, a "lines add to / the invoice
// says" check, and a confirm step that names a printed-total difference. The
// sheet is appended inside the review's own root, so closing the review (or a
// sign-out clearing the host) tears it down with it.
//
// Touch only: every control is a button, a native select or a text field of at
// least --touch-target-min; nothing depends on hover and nothing is dragged.
// Money is shown from integer cents. The one thing a person types is, for a
// held-back line, the quantity and unit cost printed on the vendor's invoice
// (an external fact, not a transaction amount), parsed by a tested helper.

import type {
  InvoicePriceReading,
  InvoiceReviewActionBody,
  InvoiceReviewDto,
  InvoiceReviewLineDto,
} from "./api.ts";
import {
  confirmButtonState,
  describeConfirmedBy,
  describeCostOutcome,
  describeEstimatedVersusFinal,
} from "./invoiceConfirmDisplay.ts";
import { describeDraftDuplicate, formatDraftMoney } from "./invoiceDraftDisplay.ts";
import {
  KIND_TILES,
  NOT_PART_OF_COST,
  buildEstimatedVersusFinal,
  describeAmountRead,
  describeConfirmStep,
  describeKindChip,
  describeLeftOutLine,
  describeOtherCount,
  describePrintedNegative,
  describeSplitOfChange,
  formatEffect,
  printedCheckFromConfirmed,
  printedCheckFromReview,
  tableInputFromConfirmed,
  tableInputFromReview,
} from "./invoiceLineKindsDisplay.ts";
import {
  describeAttentionCount,
  describeAttentionReason,
  describeDifference,
  describeFlag,
  describeHeldBackCount,
  describeHoldBackReason,
  describeMatchedCount,
  describeMatchSource,
  describeVarianceRule,
  parseDollarsToCents,
  parseQuantityText,
} from "./invoiceReviewDisplay.ts";

export type ReviewActionResult =
  { ok: true; review: InvoiceReviewDto } | { ok: false; message: string };

/** The request that confirmed an invoice, kept while the file's retention lock is
 * pending so "Lock the file" repeats it with the same key (nothing is recorded
 * twice). */
export interface PendingLock {
  key: string;
  token: string;
}

export type ConfirmResult =
  | { ok: true; review: InvoiceReviewDto; retention: "locked" | "pending" }
  | { ok: false; message: string };

export interface ReviewViewContext {
  review: InvoiceReviewDto;
  /** Prefixes every data-testid, so the two mounts of the card stay distinct. */
  testPrefix: string;
  /** Holds inv.receipt.record (and inv.cost.view): may match and unmatch. */
  canAct: boolean;
  /** Holds purchasing.invoice.confirm (and inv.cost.view): may confirm. */
  canConfirm: boolean;
  onBack: () => void;
  onAction: (lineNumber: number, body: InvoiceReviewActionBody) => Promise<ReviewActionResult>;
  /** Confirms with the review's token and this tap's idempotency key. */
  onConfirm: (token: string, key: string) => Promise<ConfirmResult>;
  /** The review changed (an action or the confirm succeeded): redraw from this
   * one, carrying the pending retention lock when there is one. */
  onUpdated: (review: InvoiceReviewDto, pendingLock?: PendingLock) => void;
  /** Set after a confirm whose file lock could not be applied yet. */
  pendingLock?: PendingLock;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  testId?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (testId !== undefined) {
    node.dataset["testid"] = testId;
  }
  return node;
}

function text<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  value: string,
  testId?: string,
): HTMLElementTagNameMap[K] {
  const node = el(tag, className, testId);
  node.textContent = value;
  return node;
}

/** A label above a value: one cell of a row's figures strip. */
function figure(label: string, value: string, className = "", testId?: string): HTMLElement {
  const cell = el("div", "review-figure");
  cell.append(text("span", "review-figure-label", label));
  cell.append(text("span", `review-figure-value ${className}`.trim(), value, testId));
  return cell;
}

function itemName(line: InvoiceReviewLineDto): string {
  const description = line.description ?? "";
  const code = line.vendorItemCode ?? "";
  return description.length > 0 ? description : code.length > 0 ? code : "Not read";
}

function quantityAndCost(line: InvoiceReviewLineDto): string {
  const quantity = line.invoiceQuantity ?? "Not read";
  const unit =
    line.invoiceUnitCostCents === null
      ? "Not read"
      : `${formatDraftMoney(line.invoiceUnitCostCents)} each`;
  return `${quantity} · ${unit}`;
}

export function renderInvoiceReview(ctx: ReviewViewContext): HTMLElement {
  const { review } = ctx;
  const confirmedRecord = review.confirmed;
  // A confirmed invoice is a record: no line can be matched or changed any more.
  const canAct = ctx.canAct && confirmedRecord === null;
  const t = (name: string): string => `${ctx.testPrefix}-review-${name}`;
  const root = el("div", "invoice-review", t("screen"));

  // Header: the title and the way back.
  const header = el("div", "review-header");
  header.append(
    text(
      "h3",
      "review-title",
      review.invoiceNumber === null ? "Review invoice" : `Review invoice #${review.invoiceNumber}`,
      t("title"),
    ),
  );
  const back = el("button", "btn-secondary review-back", t("back"));
  back.type = "button";
  back.textContent = "Back to invoices";
  back.addEventListener("click", () => ctx.onBack());
  header.append(back);
  root.append(header);
  root.append(
    text(
      "p",
      "review-subtitle",
      `${review.vendorName} · ${review.originalFilename}`,
      t("subtitle"),
    ),
  );

  // The whole-invoice duplicate banner, ahead of every section.
  if (review.duplicateInvoiceNumber !== null) {
    const words = describeDraftDuplicate(review.invoiceNumber, review.duplicateInvoiceNumber);
    const banner = el("div", "invoice-dup-warning review-duplicate", t("duplicate"));
    banner.setAttribute("role", "status");
    banner.append(text("strong", "", words.title));
    banner.append(
      document.createTextNode(
        `${words.detail} Confirm is blocked until this is resolved; nothing below applies to the invoice total yet.`,
      ),
    );
    root.append(banner);
  }

  // PO7-S4. The record of a confirmed invoice, ahead of the lines: who and when,
  // the order's estimate beside the final invoice figure (P0.12 "estimated totals
  // are visibly distinguished from final invoice costs"), and per line what
  // happened to the item's cost. Every figure is the server's, read back.
  if (confirmedRecord !== null) {
    const words = describeEstimatedVersusFinal(confirmedRecord);
    const done = el("section", "review-confirmed", t("confirmed"));
    done.setAttribute("role", "status");
    done.append(
      text("h4", "review-confirmed-title", "Invoice confirmed", t("confirmed-title")),
      text("p", "review-confirmed-by", describeConfirmedBy(confirmedRecord), t("confirmed-by")),
    );
    const compare = el("div", "review-confirmed-totals", t("confirmed-totals"));
    const total = (label: string, value: string, testId: string): HTMLElement => {
      const cell = el("div", "review-total");
      cell.append(
        text("div", "review-total-label", label),
        text("div", "review-total-value", value, testId),
      );
      return cell;
    };
    compare.append(
      total("Estimated (the order)", words.estimated, t("confirmed-estimated")),
      total("Final (the invoice)", words.final, t("confirmed-final")),
      total("Difference", words.difference, t("confirmed-difference")),
    );
    done.append(compare);
    if (words.note !== null) {
      done.append(text("p", "review-total-note", words.note, t("confirmed-note")));
    }
    const split = describeSplitOfChange(confirmedRecord);
    if (split !== null) {
      done.append(text("p", "review-total-note", split, t("confirmed-split")));
    }
    const list = el("ul", "review-confirmed-lines", t("confirmed-lines"));
    for (const confirmedLine of confirmedRecord.lines) {
      const item = el("li", "review-confirmed-line", t("confirmed-line"));
      item.dataset["lineNumber"] = String(confirmedLine.lineNumber);
      item.append(
        text("div", "review-confirmed-product", confirmedLine.productName),
        text(
          "div",
          "review-confirmed-figures",
          `${confirmedLine.estimateUnitCostCents === null ? "No estimate" : `Estimated ${formatDraftMoney(confirmedLine.estimateUnitCostCents)}`} · Invoice ${formatDraftMoney(confirmedLine.unitCostCents)}`,
          t("confirmed-line-figures"),
        ),
        text(
          "div",
          `review-confirmed-outcome review-outcome-${confirmedLine.costOutcome}`,
          describeCostOutcome(confirmedLine),
          t("confirmed-line-outcome"),
        ),
      );
      if (Number(confirmedLine.bottlesPerOrderUnit ?? 0) > 1) {
        const unit = confirmedLine.orderUnit ?? "unit";
        item.append(
          text(
            "div",
            "review-confirmed-reading",
            confirmedLine.priceReading === "per_bottle" && confirmedLine.readUnitCostCents !== null
              ? `Priced per bottle: ${formatDraftMoney(confirmedLine.readUnitCostCents)} a bottle.`
              : `Priced per ${unit}: ${formatDraftMoney(confirmedLine.unitCostCents)} a ${unit}.`,
            t("confirmed-line-reading"),
          ),
        );
      }
      list.append(item);
    }
    done.append(list);
    if (ctx.pendingLock !== undefined) {
      const pending = ctx.pendingLock;
      const lock = el("div", "review-lock-pending", t("lock-pending"));
      lock.setAttribute("role", "alert");
      lock.append(
        text(
          "p",
          "review-lock-text",
          "The invoice is confirmed, but its file could not be locked against deletion yet.",
        ),
      );
      const retry = el("button", "btn-secondary review-lock-button", t("lock-retry"));
      retry.type = "button";
      retry.textContent = "Lock the file";
      retry.addEventListener("click", () => {
        retry.disabled = true;
        void ctx.onConfirm(pending.token, pending.key).then((result) => {
          if (result.ok) {
            ctx.onUpdated(result.review, result.retention === "pending" ? pending : undefined);
          } else {
            retry.disabled = false;
            lock.append(text("p", "admin-error", result.message));
          }
        });
      });
      lock.append(retry);
      done.append(lock);
    }
    root.append(done);
  }

  // One inline correction panel at a time, under the row it belongs to.
  let openPanel: { line: number; node: HTMLElement } | null = null;
  const closePanel = (): void => {
    openPanel?.node.remove();
    openPanel = null;
  };

  const buildPanel = (line: InvoiceReviewLineDto): HTMLElement => {
    const panel = el("div", "review-edit", t("edit"));
    panel.dataset["lineNumber"] = String(line.lineNumber);
    const error = el("p", "admin-error review-edit-error", t("edit-error"));
    error.hidden = true;
    error.setAttribute("role", "alert");
    const fields = el("div", "review-edit-fields");

    const selectId = `${ctx.testPrefix}-review-select-${line.lineNumber}`;
    const selectWrap = el("div", "review-edit-field");
    const selectLabel = text("label", "review-edit-label", "Order line");
    selectLabel.htmlFor = selectId;
    const select = el("select", "review-edit-select", t("edit-line"));
    select.id = selectId;
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "Choose a line on this order";
    select.append(placeholder);
    for (const orderLine of review.orderLines) {
      const option = document.createElement("option");
      option.value = orderLine.id;
      option.textContent =
        `${orderLine.productName} (ordered ${orderLine.orderedQuantity}, ` +
        `received ${orderLine.receivedQuantity})`;
      select.append(option);
    }
    select.value = line.match.purchaseOrderLineId ?? line.match.candidatePurchaseOrderLineId ?? "";
    selectWrap.append(selectLabel, select);
    fields.append(selectWrap);

    // A held-back line is entered by hand: the quantity and unit cost the
    // vendor's invoice shows. Both, or neither.
    let quantityInput: HTMLInputElement | null = null;
    let costInput: HTMLInputElement | null = null;
    if (line.bucket === "held_back") {
      const quantityId = `${ctx.testPrefix}-review-quantity-${line.lineNumber}`;
      const quantityWrap = el("div", "review-edit-field");
      const quantityLabel = text("label", "review-edit-label", "Quantity on the invoice");
      quantityLabel.htmlFor = quantityId;
      quantityInput = el("input", "review-edit-input", t("edit-quantity"));
      quantityInput.id = quantityId;
      quantityInput.type = "text";
      quantityInput.inputMode = "decimal";
      quantityInput.autocomplete = "off";
      quantityInput.value = line.invoiceQuantity ?? "";
      quantityWrap.append(quantityLabel, quantityInput);

      const costId = `${ctx.testPrefix}-review-cost-${line.lineNumber}`;
      const costWrap = el("div", "review-edit-field");
      const costLabel = text("label", "review-edit-label", "Unit cost on the invoice ($)");
      costLabel.htmlFor = costId;
      costInput = el("input", "review-edit-input", t("edit-cost"));
      costInput.id = costId;
      costInput.type = "text";
      costInput.inputMode = "decimal";
      costInput.autocomplete = "off";
      costInput.value =
        line.invoiceUnitCostCents === null
          ? ""
          : formatDraftMoney(line.invoiceUnitCostCents).replace("$", "").replace(/,/g, "");
      costWrap.append(costLabel, costInput);
      fields.append(quantityWrap, costWrap);
    }
    panel.append(fields, error);

    const actions = el("div", "review-edit-actions");
    const apply = el("button", "btn-primary review-edit-button", t("edit-apply"));
    apply.type = "button";
    apply.textContent = line.bucket === "held_back" ? "Enter this line" : "Match to this line";
    const unmatch = el("button", "btn-secondary review-edit-button", t("edit-unmatch"));
    unmatch.type = "button";
    unmatch.textContent = "Mark unmatched";
    const cancel = el("button", "btn-secondary review-edit-button", t("edit-cancel"));
    cancel.type = "button";
    cancel.textContent = "Cancel";
    actions.append(apply, unmatch);
    if (line.bucket === "held_back") {
      // PO7-S4-F1: a held-back line can also be classified, after Enter by hand.
      const kindOpen = kindSheetButton(line, "Fee, credit or tax", false);
      if (kindOpen !== null) {
        kindOpen.classList.add("review-edit-button");
        actions.append(kindOpen);
      }
    }
    actions.append(cancel);
    panel.append(actions);

    const fail = (message: string): void => {
      error.textContent = message;
      error.hidden = false;
    };
    const setBusy = (busy: boolean): void => {
      apply.disabled = busy;
      unmatch.disabled = busy;
    };
    const send = async (body: InvoiceReviewActionBody): Promise<void> => {
      error.hidden = true;
      setBusy(true);
      const result = await ctx.onAction(line.lineNumber, body);
      setBusy(false);
      if (result.ok) {
        ctx.onUpdated(result.review);
      } else {
        fail(result.message);
      }
    };
    apply.addEventListener("click", () => {
      if (select.value === "") {
        fail("Choose the line on this order that this invoice line belongs to.");
        return;
      }
      const body: InvoiceReviewActionBody = {
        action: "match",
        expectedVersion: line.version,
        purchaseOrderLineId: select.value,
        // PO7-S4-F4: keeping the same order line keeps the reviewer's price reading.
        ...(line.priceReading === "per_bottle" && select.value === line.match.purchaseOrderLineId
          ? { priceReading: "per_bottle" as const }
          : {}),
      };
      if (quantityInput !== null && costInput !== null) {
        const quantity = parseQuantityText(quantityInput.value);
        const cents = parseDollarsToCents(costInput.value);
        if (quantity === null) {
          fail("Enter the quantity the invoice shows, for example 3 or 2.5.");
          return;
        }
        if (cents === null) {
          fail("Enter the unit cost the invoice shows in dollars and cents, for example 9.50.");
          return;
        }
        body.quantity = quantity;
        body.unitPriceCents = cents;
      }
      void send(body);
    });
    unmatch.addEventListener("click", () => {
      void send({ action: "unmatch", expectedVersion: line.version });
    });
    cancel.addEventListener("click", () => closePanel());
    return panel;
  };

  const openFor = (line: InvoiceReviewLineDto, row: HTMLElement): void => {
    const already = openPanel?.line === line.lineNumber;
    closePanel();
    if (already) {
      return;
    }
    const node = buildPanel(line);
    row.after(node);
    openPanel = { line: line.lineNumber, node };
    node.querySelector<HTMLElement>("select")?.focus();
  };

  const actionButton = (
    line: InvoiceReviewLineDto,
    row: HTMLElement,
    label: string,
  ): HTMLButtonElement | null => {
    if (!canAct) {
      return null;
    }
    const button = el("button", "btn-secondary review-row-button", t("action"));
    button.type = "button";
    button.textContent = label;
    button.setAttribute("aria-label", `${label}: ${itemName(line)}`);
    button.addEventListener("click", () => openFor(line, row));
    return button;
  };

  // PO7-S4-F1. The kind sheet: a bottom sheet on a tablet, a centred dialog on a
  // laptop. Nothing is preselected and nothing is typed, except Fix amount, the
  // dollars printed on the vendor's invoice (an external fact, as the held-back
  // panel's quantity and unit cost are).
  let openSheet: HTMLElement | null = null;
  const closeSheet = (): void => {
    openSheet?.remove();
    openSheet = null;
  };
  const openKindSheet = (line: InvoiceReviewLineDto, reopened: boolean): void => {
    closeSheet();
    const wrap = el("div", "review-sheet-wrap", t("kind-sheet"));
    wrap.dataset["lineNumber"] = String(line.lineNumber);
    const scrim = el("div", "review-sheet-scrim");
    const sheet = el("div", "review-sheet", t("kind-dialog"));
    sheet.setAttribute("role", "dialog");
    sheet.setAttribute("aria-modal", "true");
    sheet.setAttribute("aria-label", "What kind of line is this?");
    sheet.append(text("h4", "review-sheet-title", "What kind of line is this?"));

    const lineRow = el("p", "review-sheet-line", t("kind-line"));
    lineRow.append(document.createTextNode(`${itemName(line)} · ${describeAmountRead(line)} · `));
    const fixButton = el("button", "btn-secondary review-sheet-fix", t("kind-fix"));
    fixButton.type = "button";
    fixButton.textContent = "Fix amount";
    lineRow.append(fixButton);
    sheet.append(lineRow);
    const negative = describePrintedNegative(line);
    if (negative !== null) {
      sheet.append(text("p", "review-sheet-warning", negative, t("kind-negative")));
    }

    // Fix amount: shown on tap, parsed by the tested dollars helper.
    let fixedCents: number | null = null;
    const fixRow = el("div", "review-sheet-fixrow", t("kind-fix-row"));
    fixRow.hidden = true;
    const fixId = `${ctx.testPrefix}-review-kind-amount-${line.lineNumber}`;
    const fixLabel = text("label", "review-edit-label", "Amount on the invoice ($)");
    fixLabel.htmlFor = fixId;
    const fixInput = el("input", "review-edit-input", t("kind-amount"));
    fixInput.id = fixId;
    fixInput.type = "text";
    fixInput.inputMode = "decimal";
    fixInput.autocomplete = "off";
    fixInput.value =
      line.amountCents !== null || line.printedAmountCents !== null
        ? formatDraftMoney(Math.abs(line.amountCents ?? line.printedAmountCents ?? 0))
            .replace("$", "")
            .replace(/,/g, "")
        : "";
    fixRow.append(fixLabel, fixInput);
    sheet.append(fixRow);

    const error = el("p", "admin-error review-sheet-error", t("kind-error"));
    error.hidden = true;
    error.setAttribute("role", "alert");

    const grid = el("div", "review-kind-grid");
    const tiles: HTMLButtonElement[] = [];
    const send = async (body: InvoiceReviewActionBody): Promise<void> => {
      error.hidden = true;
      for (const tile of tiles) {
        tile.disabled = true;
      }
      const result = await ctx.onAction(line.lineNumber, body);
      for (const tile of tiles) {
        tile.disabled = false;
      }
      if (result.ok) {
        closeSheet();
        ctx.onUpdated(result.review);
      } else {
        error.textContent = result.message;
        error.hidden = false;
      }
    };
    fixButton.addEventListener("click", () => {
      fixRow.hidden = !fixRow.hidden;
      if (!fixRow.hidden) {
        fixInput.focus();
      }
    });
    const readFixed = (): boolean => {
      fixedCents = null;
      if (fixRow.hidden) {
        return true;
      }
      const cents = parseDollarsToCents(fixInput.value);
      if (cents === null || cents <= 0) {
        error.textContent =
          "Enter the amount the invoice shows in dollars and cents, for example 15.00.";
        error.hidden = false;
        return false;
      }
      fixedCents = cents;
      return true;
    };
    const tile = (
      kindName: string,
      title: string,
      sentence: string | null,
      sign: "+" | "-" | null,
      onTap: () => void,
    ): HTMLButtonElement => {
      const button = el("button", "review-kind-tile", t("kind-tile"));
      button.type = "button";
      button.dataset["kind"] = kindName;
      if (line.kind === kindName) {
        button.classList.add("review-kind-tile-current");
        button.setAttribute("aria-current", "true");
      }
      if (sign !== null) {
        button.append(
          text("span", `review-kind-sign${sign === "-" ? " review-kind-sign-minus" : ""}`, sign),
        );
      }
      const words = el("span", "review-kind-words");
      words.append(text("b", "review-kind-title", title));
      if (sentence !== null) {
        words.append(text("span", "review-kind-sentence", sentence));
      }
      button.append(words);
      if (line.suggestedKind === kindName) {
        button.append(text("span", "review-kind-suggest", "Looks like this", t("kind-suggest")));
      }
      button.addEventListener("click", onTap);
      tiles.push(button);
      return button;
    };
    for (const words of KIND_TILES) {
      grid.append(
        tile(words.kind, words.label, words.sentence, words.sign, () => {
          if (readFixed()) {
            void send({
              action: "classify",
              expectedVersion: line.version,
              kind: words.kind,
              ...(fixedCents === null ? {} : { amountCents: fixedCents }),
            });
          }
        }),
      );
    }
    if (reopened) {
      grid.append(
        tile(
          "product",
          "Product",
          "A product on the order. You will match it to an order line.",
          null,
          () => {
            void send({ action: "unmatch", expectedVersion: line.version });
          },
        ),
      );
    }
    const leave = tile("leave_out", "Leave this line out", null, null, () => {
      void send({ action: "leave_out", expectedVersion: line.version });
    });
    leave.classList.add("review-kind-leave");
    grid.append(leave);
    sheet.append(grid, error);

    const foot = el("div", "review-sheet-foot");
    foot.append(
      text(
        "span",
        "review-sheet-note",
        "Nothing is chosen for you. A line stays in Needs your attention until you tap a kind. None of these change item cost.",
      ),
    );
    const cancel = el("button", "btn-secondary review-sheet-cancel", t("kind-cancel"));
    cancel.type = "button";
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", () => closeSheet());
    foot.append(cancel);
    sheet.append(foot);
    scrim.addEventListener("click", () => closeSheet());
    sheet.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        closeSheet();
      }
    });
    wrap.append(scrim, sheet);
    root.append(wrap);
    openSheet = wrap;
    tiles[0]?.focus();
  };

  const section = (
    id: string,
    title: string,
    count: string,
    help: string,
    lines: InvoiceReviewLineDto[],
    build: (line: InvoiceReviewLineDto) => HTMLElement,
    empty: string,
  ): HTMLElement => {
    const block = el("section", "review-section", t(`section-${id}`));
    const heading = el("h4", "review-section-title");
    heading.append(
      text("span", "", title),
      text("span", "review-section-count", count, t(`count-${id}`)),
    );
    block.append(heading, text("p", "review-section-help", help));
    const group = el("div", `review-group review-group-${id}`);
    if (lines.length === 0) {
      group.append(text("p", "review-empty", empty, t(`empty-${id}`)));
    }
    for (const line of lines) {
      group.append(build(line));
    }
    block.append(group);
    return block;
  };

  const lineHead = (
    line: InvoiceReviewLineDto,
    reason: string,
    reasonClass: string,
  ): HTMLElement => {
    const head = el("div", "review-line-head");
    const name = el("div", "review-line-name");
    name.append(text("div", "review-line-product", itemName(line), t("line-name")));
    if (reason.length > 0) {
      name.append(text("div", `review-line-reason ${reasonClass}`, reason, t("line-reason")));
    }
    head.append(name);
    return head;
  };

  const kindSheetButton = (
    line: InvoiceReviewLineDto,
    label: string,
    reopened: boolean,
  ): HTMLButtonElement | null => {
    if (!canAct) {
      return null;
    }
    const button = el("button", "btn-secondary review-row-button", t("kind-open"));
    button.type = "button";
    button.textContent = label;
    button.setAttribute("aria-label", `${label}: ${itemName(line)}`);
    button.addEventListener("click", () => openKindSheet(line, reopened));
    return button;
  };

  const matched = review.lines.filter((l) => l.bucket === "matched");
  const classified = review.lines.filter((l) => l.bucket === "classified");
  const leftOut = review.lines.filter((l) => l.bucket === "left_out");
  const attention = review.lines.filter((l) => l.bucket === "needs_attention");
  const held = review.lines.filter((l) => l.bucket === "held_back");

  // PO7-S4-F4 (owner ruling 2026-10-07, invoice line units). Only a line whose order
  // unit holds more than one bottle has a price reading to choose: priced per case (the
  // default) or per bottle. The server converts; this only asks and says.
  const readingControl = (line: InvoiceReviewLineDto): HTMLElement | null => {
    if (line.bottlesPerOrderUnit === null || line.match.purchaseOrderLineId === null) {
      return null;
    }
    const unitWord = line.orderUnit === null || line.orderUnit === "" ? "unit" : line.orderUnit;
    const perBottle = line.priceReading === "per_bottle";
    const wrap = el("div", "review-reading", t("reading"));
    wrap.append(text("span", "review-reading-label", "Price on the invoice is"));
    const options: [InvoicePriceReading, string][] = [
      ["order_unit", `Priced per ${unitWord}`],
      ["per_bottle", "Priced per bottle"],
    ];
    for (const [value, label] of options) {
      const button = el("button", "review-reading-option", t("reading-option"));
      button.type = "button";
      button.textContent = label;
      button.dataset["reading"] = value;
      const current = line.priceReading === value;
      button.setAttribute("aria-pressed", current ? "true" : "false");
      if (current) {
        button.classList.add("review-reading-current");
      }
      button.disabled = !canAct;
      button.addEventListener("click", () => {
        if (current) {
          return;
        }
        // The numbers typed by hand are the numbers on the invoice: they stay as typed and
        // are read the new way.
        const typedPrice = perBottle ? line.readUnitCostCents : line.invoiceUnitCostCents;
        const body: InvoiceReviewActionBody = {
          action: "match",
          expectedVersion: line.version,
          purchaseOrderLineId: line.match.purchaseOrderLineId as string,
          priceReading: value,
          ...(line.enteredByHand && line.invoiceQuantity !== null && typedPrice !== null
            ? { quantity: line.invoiceQuantity, unitPriceCents: typedPrice }
            : {}),
        };
        wrap.querySelectorAll("button").forEach((b) => {
          b.disabled = true;
        });
        void ctx.onAction(line.lineNumber, body).then((result) => {
          if (result.ok) {
            ctx.onUpdated(result.review);
          } else {
            wrap.querySelectorAll("button").forEach((b) => {
              b.disabled = !canAct;
            });
            note.textContent = result.message;
            note.hidden = false;
          }
        });
      });
      wrap.append(button);
    }
    // Flagged (a large difference from the order), the note asks the question the reviewer
    // must answer, in the flag colour; otherwise it says what the current reading did.
    const flaggedCost = line.flags.includes("cost_variance");
    let noteText: string;
    if (perBottle && line.readUnitCostCents !== null && line.invoiceUnitCostCents !== null) {
      noteText = `${formatDraftMoney(line.readUnitCostCents)} a bottle, ${line.bottlesPerOrderUnit} to the ${unitWord}: ${formatDraftMoney(line.invoiceUnitCostCents)} a ${unitWord}.`;
    } else if (flaggedCost && line.invoiceUnitCostCents !== null && line.poUnitCostCents !== null) {
      const direction = line.invoiceUnitCostCents < line.poUnitCostCents ? "below" : "above";
      noteText = `Invoice cost ${formatDraftMoney(line.invoiceUnitCostCents)} is far ${direction} ${formatDraftMoney(line.poUnitCostCents)} a ${unitWord}. Is ${formatDraftMoney(line.invoiceUnitCostCents)} a ${unitWord} or a bottle?`;
    } else {
      noteText = "Choose Priced per bottle if the invoice shows a price for one bottle.";
    }
    const note = text(
      "span",
      `review-reading-note${flaggedCost && !perBottle ? " review-reading-note-flagged" : ""}`,
      noteText,
      t("reading-note"),
    );
    wrap.append(note);
    return wrap;
  };

  const buildMatched = (line: InvoiceReviewLineDto): HTMLElement => {
    const flagged = line.flags.length > 0;
    const row = el("div", `review-line${flagged ? " review-line-flagged" : ""}`, t("line"));
    row.dataset["lineNumber"] = String(line.lineNumber);
    const head = lineHead(line, describeMatchSource(line), "review-reason-source");
    const button = actionButton(line, row, flagged ? "Review" : "Change match");
    if (button !== null) {
      head.append(button);
    }
    row.append(head);
    const strip = el("div", "review-figures");
    strip.append(
      figure("Ordered", line.orderedQuantity ?? "", "", t("line-ordered")),
      figure("Received", line.receivedQuantity ?? "", "", t("line-received")),
      figure("Invoiced", line.invoiceQuantity ?? "", "", t("line-invoiced")),
      figure(
        "PO cost",
        line.poUnitCostCents === null ? "None on order" : formatDraftMoney(line.poUnitCostCents),
        "",
        t("line-po-cost"),
      ),
      figure(
        "Invoice cost",
        line.invoiceUnitCostCents === null
          ? "Not read"
          : formatDraftMoney(line.invoiceUnitCostCents),
        "",
        t("line-invoice-cost"),
      ),
      figure(
        "Difference",
        describeDifference(line),
        line.flags.includes("cost_variance") ? "review-diff-up" : "",
        t("line-difference"),
      ),
    );
    row.append(strip);
    const reading = readingControl(line);
    if (reading !== null) {
      row.append(reading);
    }
    if (flagged) {
      const reasons = el("ul", "review-flags", t("line-flags"));
      for (const flag of line.flags) {
        reasons.append(text("li", "review-flag", describeFlag(flag, line), t("line-flag")));
      }
      row.append(reasons);
    }
    if (line.enteredByHand) {
      row.append(
        text("p", "review-line-note", "Entered by hand from the invoice.", t("line-entered")),
      );
    }
    return row;
  };

  const buildAttention = (line: InvoiceReviewLineDto): HTMLElement => {
    const row = el("div", "review-line review-line-attention", t("line"));
    row.dataset["lineNumber"] = String(line.lineNumber);
    const head = lineHead(line, describeAttentionReason(line), "review-reason-unmatched");
    const button = actionButton(line, row, "Match item");
    const kindButton = kindSheetButton(line, "Fee, credit or tax", false);
    if (button !== null && kindButton !== null) {
      const actions = el("div", "review-row-actions");
      actions.append(button, kindButton);
      head.append(actions);
    }
    row.append(head);
    row.append(text("div", "review-line-detail", quantityAndCost(line), t("line-detail")));
    return row;
  };

  const buildHeld = (line: InvoiceReviewLineDto): HTMLElement => {
    const row = el("div", "review-line review-line-held", t("line"));
    row.dataset["lineNumber"] = String(line.lineNumber);
    const head = lineHead(line, describeHoldBackReason(line), "review-reason-held");
    const button = actionButton(line, row, "Enter by hand");
    if (button !== null) {
      head.append(button);
    }
    row.append(head);
    row.append(text("div", "review-line-detail", quantityAndCost(line), t("line-detail")));
    return row;
  };

  const buildOther = (line: InvoiceReviewLineDto): HTMLElement => {
    const counted = line.bucket === "classified";
    const row = el(
      "div",
      `review-line review-other-line${counted ? "" : " review-other-line-left"}`,
      t("other-line"),
    );
    row.dataset["lineNumber"] = String(line.lineNumber);
    row.dataset["treatment"] = counted ? "counted" : "left_out";
    const head = el("div", "review-line-head");
    const name = el("div", "review-line-name");
    name.append(text("div", "review-line-product", itemName(line), t("line-name")));
    name.append(
      text(
        "div",
        "review-line-reason",
        counted ? NOT_PART_OF_COST : describeLeftOutLine(line.amountCents),
        t("other-note"),
      ),
    );
    head.append(name);
    if (counted && line.kind !== null && line.kind !== "product" && line.effectCents !== null) {
      const effect = line.effectCents;
      const chip = text(
        "span",
        `review-kind-chip${effect < 0 ? " review-kind-chip-minus" : ""}`,
        describeKindChip(line.kind),
        t("other-chip"),
      );
      chip.dataset["kind"] = line.kind;
      const amount = text(
        "span",
        `review-other-amount${effect < 0 ? " review-other-amount-minus" : ""}`,
        formatEffect(effect),
        t("other-amount"),
      );
      head.append(chip, amount);
      const button = kindSheetButton(line, "Change kind", true);
      if (button !== null) {
        head.append(button);
      }
    } else {
      head.append(
        text("span", "review-kind-chip review-kind-chip-left", "Left out", t("other-chip")),
        text(
          "span",
          "review-other-amount",
          line.amountCents === null ? "" : formatDraftMoney(line.amountCents),
          t("other-amount"),
        ),
      );
      if (canAct) {
        const putBack = el("button", "btn-secondary review-row-button", t("put-back"));
        putBack.type = "button";
        putBack.textContent = "Put back";
        putBack.setAttribute("aria-label", `Put back: ${itemName(line)}`);
        putBack.addEventListener("click", () => {
          putBack.disabled = true;
          void ctx
            .onAction(line.lineNumber, { action: "unmatch", expectedVersion: line.version })
            .then((result) => {
              if (result.ok) {
                ctx.onUpdated(result.review);
              } else {
                putBack.disabled = false;
                row.append(text("p", "admin-error", result.message));
              }
            });
        });
        head.append(putBack);
      }
    }
    row.append(head);
    return row;
  };

  const otherLines = [...classified, ...leftOut].sort((a, b) => a.lineNumber - b.lineNumber);

  root.append(
    section(
      "matched",
      "Matched",
      describeMatchedCount(review.counts),
      describeVarianceRule(review.settings),
      matched,
      buildMatched,
      "Nothing on this invoice is matched to the order yet.",
    ),
    section(
      "attention",
      "Needs your attention",
      describeAttentionCount(review.counts.needsAttention),
      "These lines could not be matched to an order line. Match each one, mark it as a fee, credit or tax, or leave it out.",
      attention,
      buildAttention,
      "Every line is matched or classified.",
    ),
  );
  if (otherLines.length > 0) {
    root.append(
      section(
        "other",
        "Other lines",
        describeOtherCount(classified.length, leftOut.length),
        "Fees, tax, deposits, credits and returns. Each is added or taken off below, never hidden.",
        otherLines,
        buildOther,
        "",
      ),
    );
  }
  root.append(
    section(
      "held",
      "Held back",
      describeHeldBackCount(review.counts.heldBack),
      "Low confidence. These stay out of the invoice total until you enter them by hand, classify them, or leave them out.",
      held,
      buildHeld,
      "No line was held back.",
    ),
  );

  // PO7-S4-F1. Estimated versus final: every kind in use, the Left out row, and
  // the check of the lines against the printed total.
  const evf = el("section", "review-evf-block", t("evf-block"));
  evf.append(
    text("h4", "review-section-title", "Estimated versus final"),
    text(
      "p",
      "review-section-help",
      "The order estimate has products only, so every other line shows as a change. Only products can change item cost.",
    ),
  );
  const evfTable = el("table", "review-evf", t("evf"));
  const evfHead = el("thead", "");
  const headRow = el("tr", "");
  for (const label of [
    "Part of the invoice",
    "Order estimate",
    "Invoice",
    "Change",
    "Changes item cost",
  ]) {
    headRow.append(text("th", "", label));
  }
  evfHead.append(headRow);
  const evfBody = el("tbody", "");
  const tableInput =
    confirmedRecord === null
      ? tableInputFromReview(review)
      : tableInputFromConfirmed(confirmedRecord);
  for (const row of buildEstimatedVersusFinal(tableInput)) {
    const tr = el("tr", `review-evf-row review-evf-tone-${row.tone}`, t("evf-row"));
    tr.dataset["row"] = row.key;
    tr.append(
      text("td", "", row.label),
      text("td", "review-evf-none", row.estimate),
      text(
        "td",
        row.tone === "minus" ? "review-evf-minus" : "",
        row.invoice,
        t(`evf-invoice-${row.key}`),
      ),
      text(
        "td",
        row.tone === "minus" ? "review-evf-minus" : "",
        row.change,
        t(`evf-change-${row.key}`),
      ),
      text("td", "review-evf-none", row.changesCost),
    );
    evfBody.append(tr);
  }
  evfTable.append(evfHead, evfBody);
  evf.append(evfTable);
  const check =
    confirmedRecord === null
      ? printedCheckFromReview(review)
      : printedCheckFromConfirmed(confirmedRecord);
  const checkBox = el(
    "div",
    `review-printed-check review-printed-${check.tone}`,
    t("printed-check"),
  );
  checkBox.dataset["tone"] = check.tone;
  checkBox.setAttribute("role", "status");
  checkBox.append(
    text("strong", "review-printed-title", check.title, t("printed-title")),
    text("div", "review-printed-detail", check.detail, t("printed-detail")),
  );
  evf.append(checkBox);
  root.append(evf);

  // Totals, and the Confirm invoice (PO7-S4).
  const bar = el("div", "review-total-bar", t("totals"));
  const figures = el("div", "review-total-figures");
  const poTotal = el("div", "review-total");
  poTotal.append(
    text("div", "review-total-label", "PO estimated total"),
    text(
      "div",
      "review-total-value",
      formatDraftMoney(review.totals.poEstimatedCents),
      t("po-total"),
    ),
  );
  if (review.totals.poUnpricedLineCount > 0) {
    poTotal.append(
      text(
        "div",
        "review-total-note",
        review.totals.poUnpricedLineCount === 1
          ? "1 order line has no cost"
          : `${review.totals.poUnpricedLineCount} order lines have no cost`,
      ),
    );
  }
  const invoiceTotal = el("div", "review-total");
  invoiceTotal.append(
    text("div", "review-total-label", "Invoice total (all lines)"),
    text(
      "div",
      "review-total-value",
      review.totals.invoiceTotalCents === null
        ? "Not applied"
        : formatDraftMoney(review.totals.invoiceTotalCents),
      t("invoice-total"),
    ),
  );
  figures.append(poTotal, invoiceTotal);
  const confirm = el("div", "review-confirm");
  const state = confirmButtonState({
    confirm: review.confirm,
    canConfirm: ctx.canConfirm,
    confirmed: confirmedRecord !== null,
  });
  const confirmButton = el("button", "review-confirm-button", t("confirm"));
  confirmButton.type = "button";
  confirmButton.textContent = confirmedRecord === null ? "Confirm invoice" : "Invoice confirmed";
  confirmButton.disabled = !state.enabled;
  const note = text("p", "review-confirm-note", state.note, t("confirm-note"));
  confirm.append(confirmButton, note);

  // The confirm step: a financial write is never one stray tap.
  const step = el("div", "review-confirm-step", t("confirm-step"));
  step.hidden = true;
  const stepError = el("p", "admin-error review-confirm-error", t("confirm-error"));
  stepError.hidden = true;
  stepError.setAttribute("role", "alert");
  const stepActions = el("div", "review-confirm-step-actions");
  const stepWords = describeConfirmStep(review);
  const stepConfirm = el("button", "btn-primary review-confirm-yes", t("confirm-yes"));
  stepConfirm.type = "button";
  stepConfirm.textContent = stepWords.button;
  step.dataset["difference"] = stepWords.hasDifference ? "true" : "false";
  const stepCancel = el("button", "btn-secondary review-confirm-no", t("confirm-no"));
  stepCancel.type = "button";
  stepCancel.textContent = stepWords.hasDifference ? "Go back" : "Cancel";
  stepActions.append(stepConfirm, stepCancel);
  step.append(
    text("h4", "review-confirm-step-title", stepWords.title, t("confirm-step-title")),
    text("p", "review-confirm-step-text", stepWords.body, t("confirm-step-text")),
    stepError,
    stepActions,
  );
  confirmButton.addEventListener("click", () => {
    if (!state.enabled) {
      return;
    }
    stepError.hidden = true;
    step.hidden = false;
    confirmButton.disabled = true;
    stepConfirm.focus();
  });
  stepCancel.addEventListener("click", () => {
    step.hidden = true;
    confirmButton.disabled = !state.enabled;
    confirmButton.focus();
  });
  // One key for this confirm, kept across a retry of the same tap.
  const key = crypto.randomUUID();
  stepConfirm.addEventListener("click", () => {
    stepConfirm.disabled = true;
    stepCancel.disabled = true;
    stepError.hidden = true;
    void ctx.onConfirm(review.confirm.token, key).then((result) => {
      stepConfirm.disabled = false;
      stepCancel.disabled = false;
      if (result.ok) {
        ctx.onUpdated(
          result.review,
          result.retention === "pending" ? { key, token: review.confirm.token } : undefined,
        );
      } else {
        stepError.textContent = result.message;
        stepError.hidden = false;
      }
    });
  });
  bar.append(figures, confirm);
  // The confirm step sits under the location at full width (po7-s4-f1-3-difference-confirm).
  root.append(bar, step);
  return root;
}
